<template>
    <div class="chart-container">
        <div class="flex">
            <span class="font-bold mt-2 mr-2 ml-2 text-sm">Period:</span>
            <select class="form-control form-control-sm form-control-fixed bg-white h-8" @change="updateAggregation" v-model="aggregation">
                <option :value="aggregation.aggregation" v-for="aggregation in aggregations">{{ aggregation.label }}</option>
            </select>
        </div>
        <div style="width: 100%; height: 90%" class="chart-instance" :id="id"></div>
    </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, nextTick, inject } from 'vue';
import importedAggregations from '../../chart-aggregations';

const props = defineProps({
    symbol: String,
    section: String,
    defaultTemplate: Object,
    index: Number
});

const eventBus = inject('eventBus');
const emit = defineEmits(['new-chart']);

// global chart iunstance
let gChart = null;

const id = ref(null);

// Process aggregations based on section
function normalizedAggregations() {
    return props.section !== 'futures'
        ? importedAggregations.filter(mAggregation => mAggregation.aggregation.spec === 'None')
        : importedAggregations;
}

const aggregations = ref(normalizedAggregations());
const aggregation = ref(importedAggregations[0].aggregation);

const random = (max) => {
    return Math.floor(Math.random() * Math.floor(max));
};

const updateAggregation = () => {
    gChart.change({
        id: 'Aggregation',
        context: aggregation.value
    });

    updateMainPlot();
    reApplyDensity();
    saveAggregationToLocalStorage(aggregation.value);
};

function updateIndicator(indicator) {
    gChart.change(indicator);
}

function updateSettings(settings) {
    gChart.change(...settings);
}

/**
 * I think this is a bug in the charts
 * Density will get overwritten, so we have to reapply when
 * updating the aggregation of the chart
 */
function reApplyDensity() {
    if (props.defaultTemplate &&
        props.defaultTemplate.template &&
        props.defaultTemplate.template.display &&
        props.defaultTemplate.template.display.density
    ) {

        gChart.change({
            id: 'Period',
            context: {
                density: props.defaultTemplate.template.display.density
            }
        });
    }
}

function updateMainPlot() {
    gChart.change({
        id: 'MainPlot',
        context: {
            symbol: props.symbol
        }
    });
}

function applyTemplate(pTemplate) {
    gChart.load(pTemplate, {
        keepOldAggregation: true,
        keepNewBarWidth: true
    });

    updateMainPlot();
}

function saveAggregationToLocalStorage(aggregation) {
    localStorage.setItem(`grid-view-aggregation-${props.index}`, JSON.stringify(aggregation));
}

function getInitialAggregation() {
    const item = localStorage.getItem(`grid-view-aggregation-${props.index}`);

    if (item) {
        return JSON.parse(item);
    }
}

onMounted(() => {
    id.value = `chart-${random(99999999)}`;

    nextTick(() => {
        gChart = window.MainFeed.addChart(id.value, { symbol: props.symbol });

        if (props.defaultTemplate && props.defaultTemplate.template) {
            applyTemplate(JSON.stringify(props.defaultTemplate.template));
        }

        const initialAggregation = getInitialAggregation();

        if (initialAggregation) {
            gChart.change({ id: 'Aggregation', context: initialAggregation });
            aggregation.value = initialAggregation;
        }

        nextTick(() => {
            emit('new-chart', gChart);
        });
    });

    eventBus.on('grid-settings-updated', updateSettings);
    eventBus.on('grid-indicator-changed', updateIndicator);
    eventBus.on('grid-apply-template', applyTemplate);
    // eventBus.on('grid-reset-charts', reset);
});

onUnmounted(() => {
    nextTick(() => {
        window.MainFeed.removeChart(id.value);
        gChart = null;
    });

    // Unsubscribe from EventBus events
    eventBus.off('grid-settings-updated', updateSettings);
    eventBus.off('grid-indicator-changed', updateIndicator);
    eventBus.off('grid-apply-template', applyTemplate);
    // eventBus.off('grid-reset-charts', reset);
});
</script>

